import React, { useState, useEffect, useCallback } from 'react';
import { getAdminStats, getTreasuryBalance, getPendingKYC, getPendingWithdrawals } from '../../services/api';
import { UsersIcon, CurrencyDollarIcon, ClockIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [treasuryBalance, setTreasuryBalance] = useState(0);
  const [pendingKYC, setPendingKYC] = useState([]);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchAdminData = useCallback(async () => {
    try {
      const [statsRes, treasuryRes, kycRes, withdrawalsRes] = await Promise.all([
        getAdminStats(),
        getTreasuryBalance(),
        getPendingKYC(),
        getPendingWithdrawals(),
      ]);

      if (statsRes.data.success) setStats(statsRes.data.stats);
      if (treasuryRes.data.success) setTreasuryBalance(treasuryRes.data.treasury_balance);
      if (kycRes.data.success) setPendingKYC(kycRes.data.pending);
      if (withdrawalsRes.data.success) setPendingWithdrawals(withdrawalsRes.data.withdrawals);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const isAdmin =
      sessionStorage.getItem('admin') !== null ||
      localStorage.getItem('adminLoggedIn') === 'true';

    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }

    fetchAdminData();
  }, [navigate, fetchAdminData]);

  const statCards = [
    {
      label: 'Total Users',
      value: stats?.total_users || 0,
      icon: UsersIcon,
      color: 'text-blue-500',
      bg: 'rgba(59, 130, 246, 0.12)',
    },
    {
      label: 'Total Deposits',
      value: `$${stats?.total_deposits?.toFixed(2) || 0}`,
      icon: CurrencyDollarIcon,
      color: 'text-emerald-500',
      bg: 'rgba(16, 185, 129, 0.12)',
    },
    {
      label: 'Total P&L',
      value: `$${stats?.total_pnl?.toFixed(2) || 0}`,
      icon: ChartBarIcon,
      color: stats?.total_pnl >= 0 ? 'text-emerald-500' : 'text-rose-500',
      bg: stats?.total_pnl >= 0 ? 'rgba(16, 185, 129, 0.12)' : 'rgba(244, 63, 94, 0.12)',
    },
    {
      label: 'Pending Actions',
      value: (pendingKYC.length + pendingWithdrawals.length) || 0,
      icon: ClockIcon,
      color: 'text-amber-500',
      bg: 'rgba(245, 158, 11, 0.12)',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-400 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page fade-in">
      <div className="dashboard-head">
        <h1 className="dashboard-page-title">Admin Dashboard</h1>
        <p className="dashboard-section-subtitle">Overview of platform users, treasury, and pending actions</p>
      </div>

      <section className="card dashboard-section">
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">Overview</h2>
          <p className="dashboard-section-subtitle">Key platform statistics</p>
        </div>

        <div className="admin-stats-grid">
          {statCards.map((stat, index) => (
            <div key={index} className="admin-stat-card">
              <div className="admin-stat-top">
                <div className="admin-stat-icon-wrap" style={{ background: stat.bg }}>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
              </div>

              <div className="admin-stat-body">
                <p className="admin-stat-label">{stat.label}</p>
                <p className="admin-stat-value">{stat.value}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="card dashboard-section">
        <div className="dashboard-section-body">
          <div className="admin-treasury-card">
            <div className="admin-treasury-left">
              <div className="admin-treasury-icon">
                <CurrencyDollarIcon className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="admin-treasury-title">Treasury Wallet Balance</p>
                <p className="admin-treasury-subtitle">Available funds in main wallet</p>
              </div>
            </div>
            <div className="admin-treasury-value">{treasuryBalance.toFixed(2)} USDT</div>
          </div>
        </div>
      </section>

      <div className="dashboard-bottom-grid">
        <section className="card dashboard-section">
          <div className="dashboard-section-header">
            <div className="admin-panel-head">
              <div>
                <h2 className="dashboard-section-title">Pending KYC</h2>
                <p className="dashboard-section-subtitle">Awaiting verification</p>
              </div>
              <span className="admin-panel-count">{pendingKYC.length}</span>
            </div>
          </div>

          <div className="dashboard-section-body">
            {pendingKYC.length === 0 ? (
              <div className="styled-empty-state">
                <div className="empty-state-icon">
                  <svg
                    className="h-5 w-5 flex-none text-emerald-500"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="styled-empty-title">All caught up</p>
                <p className="styled-empty-subtitle">No pending KYC submissions</p>
              </div>
            ) : (
              <div className="styled-list-wrap">
                {pendingKYC.slice(0, 5).map((kyc) => (
                  <div key={kyc.id} className="styled-list-card">
                    <div>
                      <p className="text-sm font-medium text-white">{kyc.full_name}</p>
                      <p className="text-[11px] text-gray-500">{kyc.email || kyc.phone}</p>
                    </div>
                    <span className="admin-pending-badge">Waiting</span>
                  </div>
                ))}
                {pendingKYC.length > 5 && (
                  <p className="admin-more-note">+{pendingKYC.length - 5} more</p>
                )}
              </div>
            )}
          </div>
        </section>

        <section className="card dashboard-section">
          <div className="dashboard-section-header">
            <div className="admin-panel-head">
              <div>
                <h2 className="dashboard-section-title">Pending Withdrawals</h2>
                <p className="dashboard-section-subtitle">Requests to review</p>
              </div>
              <span className="admin-panel-count">{pendingWithdrawals.length}</span>
            </div>
          </div>

          <div className="dashboard-section-body">
            {pendingWithdrawals.length === 0 ? (
              <div className="styled-empty-state">
                <div className="empty-state-icon">
                  <svg
                    className="h-5 w-5 flex-none text-emerald-500"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="styled-empty-title">All cleared</p>
                <p className="styled-empty-subtitle">No pending withdrawal requests</p>
              </div>
            ) : (
              <div className="styled-list-wrap">
                {pendingWithdrawals.slice(0, 5).map((wd) => (
                  <div key={wd.id} className="styled-list-card">
                    <div>
                      <p className="text-sm font-medium text-white">{parseFloat(wd.amount).toFixed(2)} USDT</p>
                      <p className="text-[11px] text-gray-500 font-mono">
                        {wd.destination_address.slice(0, 12)}...
                      </p>
                    </div>
                    <span className="admin-pending-badge">Pending</span>
                  </div>
                ))}
                {pendingWithdrawals.length > 5 && (
                  <p className="admin-more-note">+{pendingWithdrawals.length - 5} more</p>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;